import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import JobProcess from 'models/JobProcess';
import { JobProcessStatus } from 'models/JobProcess';
import { JOB_PROCESS_CHANNEL } from 'services/api/constants';
import { showExport } from 'services/api/exports';
import { useToastActions } from 'stores/toastStore';

import { useWebsocketSubscription } from './useWebsocketSubscription';

type JobProcessPayload = {
  id: string;
  status: string;
  error: string | null;
  context: Record<string, unknown>;
  type: string;
};

const toastMsg = { text: 'Something went wrong with the export. Please try again.' };

export default function useWebsocketExport(exportFn: any) {
  const { addToast } = useToastActions();
  const [subscriptionParams, setSubscriptionParams] = useState<{ job_process_id: string } | undefined>();
  const [exportId, setExportId] = useState<string>();
  const [exporting, setExporting] = useState(false);

  const { mutate } = useMutation({
    mutationFn: exportFn,
    onMutate: () => {
      setExporting(true);
    },
    onSuccess: (res: JobProcess) => {
      setSubscriptionParams({ job_process_id: res.id });
    },
    onError: () => {
      addToast(toastMsg);
      setExporting(false);
    },
  });

  const { subscription } = useWebsocketSubscription<JobProcessPayload>({
    channel: JOB_PROCESS_CHANNEL,
    params: subscriptionParams,
    enabled: !!subscriptionParams,
    onRejected: () => {
      addToast(toastMsg);
      setExporting(false);
      setSubscriptionParams(undefined);
    },
    onReceived: (data) => {
      if (data.status === JobProcessStatus.Error) {
        addToast(toastMsg);
        setExporting(false);
        setSubscriptionParams(undefined);
        subscription?.unsubscribe();
        return;
      }

      if (data.status === JobProcessStatus.Completed) {
        setSubscriptionParams(undefined);
        const context = data.context as any;
        setExportId(context.exportId);
        subscription?.unsubscribe();
      }
    },
  });

  const { data: exportInfo, isError: exportError } = useQuery({
    queryKey: ['export', exportId],
    queryFn: () => showExport(exportId as string),
    enabled: !!exportId,
  });

  useEffect(() => {
    if (exportInfo?.url) {
      setExporting(false);
      window.open(exportInfo.url, '_blank');
      setExportId(undefined);
    }
  }, [exportInfo]);

  useEffect(() => {
    if (exportError) {
      addToast({ text: 'Something went wrong with the export. Please try again.' });
      setExporting(false);
      setExportId(undefined);
    }
  }, [addToast, exportError]);

  return {
    exporting,
    exportData: exportInfo,
    initiateExport: mutate as any,
  };
}
